// src/components/crm/LeadContactsPanel.jsx
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Button, TextInput } from "flowbite-react";
import {
  HiOutlinePlus,
  HiPencil,
  HiCheck,
  HiX,
  HiOutlineTrash,
} from "react-icons/hi";
import { IoLogoWhatsapp } from "react-icons/io5";
import { MdOutlineMailOutline } from "react-icons/md";
import axios from "../../config/axiosInstance";
import { LEADS } from "../../config/apiConfig";

/* ================= Utils ================= */
function onlyDigits(v) {
  return String(v || "").replace(/\D/g, "");
}
function formatPhoneBR(v) {
  const d = onlyDigits(v);
  if (!d) return "";
  if (d.length <= 10) {
    // (11) 2345-6789
    return d.replace(/^(\d{2})(\d{4})(\d{0,4}).*/, "($1) $2-$3").trim();
  }
  // (11) 91234-5678
  return d.replace(/^(\d{2})(\d{5})(\d{0,4}).*/, "($1) $2-$3").trim();
}
function initials(name = "") {
  const parts = String(name).trim().split(/\s+/);
  const first = parts[0]?.[0] || "";
  const last = parts.length > 1 ? parts[parts.length - 1]?.[0] || "" : "";
  return (first + last).toUpperCase();
}

/* ================ UI helpers ================= */
const ActionIcon = ({ title, onClick, children, href, target }) => {
  const className =
    "inline-flex items-center justify-center rounded-lg hover:bg-gray-50 transition focus:outline-none focus:ring-2 focus:ring-indigo-200";
  if (href) {
    return (
      <a
        href={href}
        target={target}
        rel={target ? "noreferrer" : undefined}
        className={className}
        title={title}
      >
        {children}
      </a>
    );
  }
  return (
    <button type="button" onClick={onClick} className={className} title={title}>
      {children}
    </button>
  );
};

/* ================ Row (memo) =================
 * Mantém estado local de edição para evitar re-render no painel inteiro.
 */
const Row = React.memo(function Row({
  c,
  dense,
  onDelete,
  onSave, // recebe (id, payload)
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState({
    name: "",
    role: "",
    phone: "",
    email: "",
  });

  // Quando entra em edição, inicializa os campos com o contato atual
  const startEdit = useCallback(() => {
    setForm({
      name: c.name || "",
      role: c.role || "",
      phone: c.phone || "",
      email: c.email || "",
    });
    setIsEditing(true);
  }, [c]);

  const cancelEdit = useCallback(() => {
    setIsEditing(false);
  }, []);

  const handleSave = useCallback(async () => {
    const payload = {
      name: String(form.name || "").trim(),
      role: String(form.role || "").trim(),
      phone: onlyDigits(form.phone),
      email: String(form.email || "").trim(),
    };
    if (!payload.name) {
      window.alert("Informe o nome do contato.");
      return;
    }
    if (!String(payload.phone || payload.email).trim()) {
      window.alert("Informe ao menos telefone ou e-mail.");
      return;
    }
    await onSave(c.id, payload);
    setIsEditing(false);
  }, [form, onSave, c.id]);

  const telDigits = onlyDigits(c.phone);
  const mailHref = c.email ? `mailto:${c.email}` : null;
  const telHref = telDigits ? `tel:${telDigits}` : null;
  const waHref = telDigits ? `https://wa.me/${telDigits}` : null;

  if (isEditing) {
    return (
      <div
        className={`rounded-2xl border p-3 ${dense ? "mb-2" : "mb-3"} bg-white`}
      >
        <div className={`${dense ? "grid grid-cols-1 gap-2" : "grid gap-2"}`}>
          <TextInput
            placeholder="Nome do contato *"
            value={form.name}
            autoFocus
            onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
          />
          <TextInput
            placeholder="Cargo"
            value={form.role}
            onChange={(e) => setForm((s) => ({ ...s, role: e.target.value }))}
          />
          <TextInput
            placeholder="Telefone"
            inputMode="tel"
            autoComplete="tel"
            value={form.phone}
            onChange={(e) => setForm((s) => ({ ...s, phone: e.target.value }))}
            onBlur={(e) =>
              setForm((s) => ({ ...s, phone: formatPhoneBR(e.target.value) }))
            }
          />
          <TextInput
            placeholder="E-mail"
            type="email"
            value={form.email}
            onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))}
          />
        </div>
        <div className="flex items-center gap-2 mt-3 justify-end">
          <Button size="xs" onClick={handleSave}>
            <HiCheck className="mr-1" /> Salvar
          </Button>
          <Button size="xs" color="light" onClick={cancelEdit}>
            <HiX className="mr-1" /> Cancelar
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`group relative overflow-hidden rounded-2xl border p-4 ${
        dense ? "mb-2" : "mb-3"
      } bg-gradient-to-b from-white to-gray-50`}
    >
      <div className="absolute left-0 top-0 h-full w-1 bg-gradient-to-b from-indigo-500 via-fuchsia-500 to-pink-500" />
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 shrink-0 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 border flex items-center justify-center text-xs font-semibold text-gray-700">
          {initials(c.name)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <div className="font-semibold text-gray-900 truncate text-[15px]">
                  {c.name || "-"}
                </div>
              </div>
              <div className="text-xs text-gray-500 truncate">
                {c.role || "—"}
              </div>
            </div>
            <div className="shrink-0 flex items-center gap-2.5">
              <ActionIcon title="Editar" onClick={startEdit}>
                <HiPencil className="h-4 w-4" />
              </ActionIcon>
              <ActionIcon title="Remover" onClick={() => onDelete(c.id)}>
                <HiOutlineTrash className="h-4 w-4 text-red-600" />
              </ActionIcon>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-2 grid grid-cols-1 gap-x-6 gap-y-1 text-sm">
        <div className="text-gray-700 flex items-center gap-2">
          <span className="text-gray-500">Telefone:</span>
          <span className="font-medium">
            {telDigits ? formatPhoneBR(telDigits) : "—"}
          </span>
          {waHref && (
            <ActionIcon href={waHref} target="_blank" title="WhatsApp">
              <IoLogoWhatsapp className="h-4 w-4" />
            </ActionIcon>
          )}
          {telHref && (
            <ActionIcon href={telHref} title="Ligar">
              {/* opcional: um ícone de telefone */}
            </ActionIcon>
          )}
        </div>
        <div className="text-gray-700 flex items-center gap-2 min-w-0 mt-1">
          <span className="text-gray-500">E-mail:</span>
          <span className="font-medium break-all truncate">
            {c.email || "—"}
          </span>
          {mailHref && (
            <ActionIcon href={mailHref} title="Enviar e-mail">
              <MdOutlineMailOutline className="h-4 w-4 text-gray-600" />
            </ActionIcon>
          )}
        </div>
      </div>
    </div>
  );
});

/* ================ Painel ================= */
export default function LeadContactsPanel({
  leadId,
  className = "",
  dense = false,
}) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);

  // Add form
  const [showAdd, setShowAdd] = useState(false);
  const [add, setAdd] = useState({ name: "", phone: "", email: "", role: "" });

  const load = useCallback(async () => {
    if (!leadId) return;
    try {
      setLoading(true);
      const { data } = await axios.get(LEADS.CONTACTS.LIST(leadId));
      const list = Array.isArray(data?.data) ? data.data : [];
      setContacts(list);
    } catch (e) {
      console.error("Erro ao carregar contatos:", e);
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => {
    setContacts([]);
    setShowAdd(false);
    load();
  }, [leadId, load]);

  const onDelete = useCallback(
    async (id) => {
      if (!window.confirm("Remover este contato?")) return;
      try {
        await axios.delete(LEADS.CONTACTS.DELETE(leadId, id));
        await load();
      } catch (e) {
        console.error("Erro ao remover contato:", e);
        window.alert("Não foi possível remover o contato.");
      }
    },
    [leadId, load]
  );

  const onSave = useCallback(
    async (id, payload) => {
      try {
        await axios.patch(LEADS.CONTACTS.UPDATE(leadId, id), payload);
        await load();
      } catch (e) {
        console.error("Erro ao atualizar contato:", e);
        window.alert("Não foi possível atualizar o contato.");
      }
    },
    [leadId, load]
  );

  const handleAdd = useCallback(async () => {
    const payload = {
      name: String(add.name || "").trim(),
      phone: onlyDigits(add.phone),
      email: String(add.email || "").trim(),
      role: String(add.role || "").trim(),
    };
    if (!payload.name) {
      window.alert("Informe o nome do contato.");
      return;
    }
    if (!String(payload.phone || payload.email).trim()) {
      window.alert("Informe ao menos telefone ou e-mail.");
      return;
    }

    try {
      await axios.post(LEADS.CONTACTS.ADD(leadId), payload);
      setShowAdd(false);
      setAdd({ name: "", phone: "", email: "", role: "" });
      await load();
    } catch (e) {
      console.error("Erro ao adicionar contato:", e);
      window.alert("Não foi possível adicionar o contato.");
    }
  }, [add, leadId, load]);

  return (
    <div className={`${className}`}>
      <div className="flex items-center justify-between mb-2">
        <h5 className="text-sm font-semibold text-gray-800">
          Contatos do cliente ({contacts?.length || 0})
        </h5>
        <button
          type="button"
          onClick={() => setShowAdd((v) => !v)}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border text-xs bg-white hover:bg-gray-50"
        >
          <HiOutlinePlus size={14} /> Adicionar
        </button>
      </div>

      {showAdd && (
        <div className="mb-3 rounded-2xl border p-4 bg-white">
          <div
            className={`${
              dense ? "grid grid-cols-1 gap-2" : "grid grid-cols-1 gap-3"
            }`}
          >
            <TextInput
              placeholder="Nome do contato *"
              value={add.name}
              autoFocus
              onChange={(e) => setAdd((s) => ({ ...s, name: e.target.value }))}
            />
            <TextInput
              placeholder="Cargo"
              value={add.role}
              onChange={(e) => setAdd((s) => ({ ...s, role: e.target.value }))}
            />
            <TextInput
              placeholder="Telefone"
              inputMode="tel"
              autoComplete="tel"
              value={add.phone}
              onChange={(e) => setAdd((s) => ({ ...s, phone: e.target.value }))}
              onBlur={(e) =>
                setAdd((s) => ({ ...s, phone: formatPhoneBR(e.target.value) }))
              }
            />
            <TextInput
              placeholder="E-mail"
              type="email"
              value={add.email}
              onChange={(e) => setAdd((s) => ({ ...s, email: e.target.value }))}
            />
          </div>
          <div className="flex items-center justify-end gap-2 mt-3">
            <Button size="xs" onClick={handleAdd}>
              Salvar
            </Button>
            <Button
              size="xs"
              color="light"
              onClick={() => {
                setShowAdd(false);
                setAdd({ name: "", phone: "", email: "", role: "" });
              }}
            >
              Cancelar
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="space-y-2">
          {[...Array(2)].map((_, i) => (
            <div
              key={i}
              className="rounded-2xl border p-4 bg-white animate-pulse"
            >
              <div className="h-4 w-1/3 bg-gray-200 rounded mb-2" />
              <div className="h-3 w-1/2 bg-gray-100 rounded" />
            </div>
          ))}
        </div>
      ) : contacts?.length ? (
        <div>
          {contacts.map((c) => (
            <Row
              key={c.id}
              c={c}
              dense={dense}
              onDelete={onDelete}
              onSave={onSave}
            />
          ))}
        </div>
      ) : (
        <div className="rounded-2xl border border-dashed border-gray-400 py-2 px-3 bg-white text-center">
          <div className="text-sm text-gray-500 italic">
            Nenhum contato cadastrado.
          </div>
        </div>
      )}
    </div>
  );
}
